import type { SupabaseClient, SupportedStorage } from "@supabase/supabase-js"
import { createClient } from "@supabase/supabase-js"
import { Database } from "./types/database"
import { createIdbAuthStorage } from "./authStorage"

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const publishableKey = import.meta.env.VITE_SB_PUBLISHABLE_KEY
const isProd = import.meta.env.PROD

// Custom fetch that rewrites edge function URLs to relative paths.
// This allows the Vite dev server proxy to handle function calls,
// avoiding CORS issues when using tunnels (e.g., Cloudflare Tunnel for mobile testing).
const customFetch =
  (fetcher: typeof fetch): typeof fetch =>
  (input, init) => {
    if (isProd) return fetcher(input, init)

    if (typeof input === "string" && input.includes("/functions/v1/")) {
      const relativePath = input.substring(input.indexOf("/functions/v1/"))
      return fetcher(relativePath, init)
    }
    if (input instanceof Request && input.url.includes("/functions/v1/")) {
      const relativePath = input.url.substring(
        input.url.indexOf("/functions/v1/"),
      )
      return fetcher(new Request(relativePath, input), init)
    }
    return fetcher(input, init)
  }

type TransportOptions = {
  fetch?: typeof fetch
  /** Wraps the client's fetch, e.g. to observe auth responses. */
  wrapFetch?: (fetchImpl: typeof fetch) => typeof fetch
}

const clientFetch = (options?: TransportOptions) => {
  const transport = customFetch(options?.fetch ?? fetch)
  return options?.wrapFetch ? options.wrapFetch(transport) : transport
}

export const createFieldbookSupabaseClient = (
  options?: {
    authStorage?: SupportedStorage
  } & TransportOptions,
) =>
  createClient<Database>(supabaseUrl, publishableKey, {
    auth: {
      storage: options?.authStorage ?? createIdbAuthStorage(),
    },
    global: {
      fetch: clientFetch(options),
    },
  })

/**
 * A client whose every request carries exactly `accessToken` — it never
 * refreshes, swaps, or drops the token, and never falls back to `anon`. It
 * has no auth API of its own (accessing `.auth` throws). Use it when the
 * caller must know which token a request was authorised with.
 */
export const createFieldbookSupabaseClientForToken = (
  accessToken: string,
  options?: TransportOptions,
) =>
  createClient<Database>(supabaseUrl, publishableKey, {
    accessToken: async () => accessToken,
    global: {
      fetch: clientFetch(options),
    },
  })

/**
 * Where supabase-js persists the session: its default `sb-<project>-auth-token`
 * key. Logout needs it because signOut() removes the stored session only after
 * the server confirms the sign-out, so offline it leaves the session in place.
 */
export const getSupabaseAuthStorageKey = () =>
  `sb-${new URL(supabaseUrl).hostname.split(".")[0]}-auth-token`

let configuredFieldbookSupabaseClient: SupabaseClient<Database> | undefined

const getFieldbookSupabaseClient = () => {
  configuredFieldbookSupabaseClient ??= createFieldbookSupabaseClient()
  return configuredFieldbookSupabaseClient
}

export const fieldbookSupabaseClient = new Proxy({} as SupabaseClient<Database>, {
  get(_target, property, receiver) {
    const value = Reflect.get(getFieldbookSupabaseClient(), property, receiver)
    return typeof value === "function"
      ? value.bind(getFieldbookSupabaseClient())
      : value
  },
  set(_target, property, value, receiver) {
    return Reflect.set(getFieldbookSupabaseClient(), property, value, receiver)
  },
})

export const setFieldbookSupabaseClient = (
  client: SupabaseClient<Database>,
) => {
  configuredFieldbookSupabaseClient = client
}
